import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TextField from "components/common/TextField";
import {
  useAdminDeactivateIdentity,
  useAdminLookupIdentity,
} from "services/admin";
import FreeIdentityDialog from "components/admin/FreeIdentityDialog";
import {
  AdminLookupIdentityResponse,
  AdminLookupIdentityResponse_Account,
  AdminLookupIdentityResponse_Conversation,
  AdminLookupIdentityResponse_NuxEntry,
} from "protogen/admin_service_pb";
import { AccountType } from "protogen/common_pb";
import GridPage from "components/layout/GridPage";
import useIsMobile from "components/hooks/useIsMobile";

const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  [AccountType.UNKNOWN]: "unknown",
  [AccountType.ADVISOR]: "advisor",
  [AccountType.MEMBER]: "member",
  [AccountType.CONTACT]: "external contact",
};

const QUERY_TYPE_LABELS: Record<string, string> = {
  phone: "phone number",
  email: "email address",
  user_id: "user id",
};

const formatDate = (secUTC: bigint) =>
  secUTC ? new Date(Number(secUTC) * 1000).toLocaleString() : "—";

// External contacts are freed inline here; the backend enforces the same rule. Members and
// advisors are freed off-page via `freeIdentityLink` -- their action lives on the family
// admin / advisor administration profile respectively.
const canDeactivate = (account: AdminLookupIdentityResponse_Account): boolean =>
  !account.isRetired && account.stub?.accountType === AccountType.CONTACT;

// A deactivated advisor or a member of a deactivated family is freed from their own admin
// page, not inline; returns where to link, or null when that doesn't apply.
const freeIdentityLink = (
  account: AdminLookupIdentityResponse_Account,
): { url: string; label: string } | null => {
  if (account.isRetired || !account.stub) return null;
  if (
    account.stub.accountType === AccountType.ADVISOR &&
    account.advisorStatus === "deactivated"
  ) {
    return {
      url: `/tools/advisor-administration/${encodeURIComponent(
        account.stub.ref,
      )}`,
      label: "Go to advisor profile to free identity",
    };
  }
  if (
    account.stub.accountType === AccountType.MEMBER &&
    account.familyStatus.includes("deactivated") &&
    account.familyRef
  ) {
    return {
      url: `/families/${encodeURIComponent(account.familyRef)}#tab-admin`,
      label: "Go to family admin to free identity",
    };
  }
  return null;
};

const AccountCard = ({
  account,
  onDeactivate,
}: {
  account: AdminLookupIdentityResponse_Account;
  onDeactivate: () => void;
}) => {
  const navigate = useNavigate();
  const stub = account.stub;
  const name = stub?.displayName || "(no name)";
  const accountType: AccountType = stub?.accountType ?? AccountType.UNKNOWN;
  const link = freeIdentityLink(account);
  return (
    <Paper variant="outlined" sx={{ padding: "16px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6">{name}</Typography>
        <Chip size="small" label={ACCOUNT_TYPE_LABELS[accountType]} />
        {account.isRetired && (
          <Chip size="small" color="warning" label="deactivated" />
        )}
        {link ? (
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(link.url)}
            sx={{ marginLeft: "auto" }}
          >
            {link.label}
          </Button>
        ) : (
          canDeactivate(account) && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={onDeactivate}
              sx={{ marginLeft: "auto" }}
            >
              Free identity
            </Button>
          )
        )}
      </Box>

      <Table size="small" sx={{ marginTop: "12px" }}>
        <TableBody>
          <TableRow>
            <TableCell>User id</TableCell>
            <TableCell>{String(account.userId)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Username</TableCell>
            <TableCell>{account.username || "—"}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Phone</TableCell>
            <TableCell>{account.phone || "—"}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell>{account.email || "—"}</TableCell>
          </TableRow>
          {account.familyName && (
            <TableRow>
              <TableCell>Family</TableCell>
              <TableCell>
                {account.familyName} ({account.familyStatus})
              </TableCell>
            </TableRow>
          )}
          {account.advisorStatus && (
            <TableRow>
              <TableCell>Advisor status</TableCell>
              <TableCell>{account.advisorStatus}</TableCell>
            </TableRow>
          )}
          {account.addedByName && (
            <TableRow>
              <TableCell>Contact added by</TableCell>
              <TableCell>{account.addedByName}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Typography variant="subtitle2" sx={{ marginTop: "16px" }}>
        Conversations ({account.conversations.length})
      </Typography>
      {account.conversations.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None. Nothing is using this account.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Medium</TableCell>
                <TableCell>Inbox</TableCell>
                <TableCell align="right">Messages</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell>Participants</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {account.conversations.map(
                (conversation: AdminLookupIdentityResponse_Conversation) => (
                  <TableRow key={conversation.ref}>
                    <TableCell>{conversation.medium}</TableCell>
                    <TableCell>
                      {conversation.inboxType}{" "}
                      {String(conversation.inboxEntityId)}
                    </TableCell>
                    <TableCell align="right">
                      {conversation.messageCount}
                    </TableCell>
                    <TableCell>
                      {formatDate(conversation.lastUpdatedSecUTC)}
                    </TableCell>
                    <TableCell>
                      {conversation.participants
                        .map((p) => p.displayName)
                        .filter(Boolean)
                        .join(", ")}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
};

export default () => {
  const isMobile = useIsMobile();
  const [query, setQuery] = useState("");
  const [searched, setSearched] = useState("");
  const [result, setResult] = useState<AdminLookupIdentityResponse | null>(
    null,
  );
  const [pendingDeactivate, setPendingDeactivate] =
    useState<AdminLookupIdentityResponse_Account | null>(null);
  const { request, loading } = useAdminLookupIdentity((r) => setResult(r));
  const { request: deactivate, loading: deactivating } =
    useAdminDeactivateIdentity((r) => {
      const updated = r.account;
      if (updated) {
        setResult((prev) => {
          if (!prev) return prev;
          const next = prev.clone();
          next.accounts = prev.accounts.map((a) =>
            a.userId === updated.userId ? updated : a,
          );
          return next;
        });
      }
      setPendingDeactivate(null);
    });

  const onSearch = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearched(trimmed);
    await request({ query: trimmed });
  };

  const onConfirmDeactivate = async () => {
    if (!pendingDeactivate?.stub) return;
    await deactivate({ userRef: pendingDeactivate.stub.ref });
  };

  const accounts = result?.accounts ?? [];
  const nuxEntries = result?.nuxEntries ?? [];
  const isCollision = accounts.length > 1;

  return (
    <GridPage
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px" : "64px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Typography variant="title">Identity Lookup</Typography>
        <Typography>
          Shows every account holding a phone number or email address, plus its
          conversations and NUX entries. One person can hold accounts of several
          types over time, so a value reported as "already in use" may be
          claimed by more than one of them at once.
        </Typography>

        <Box sx={{ display: "flex", gap: "20px" }}>
          <TextField
            value={query}
            autoComplete="off"
            label="Phone, email, or user id"
            size="small"
            fullWidth
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSearch()}
          />
          <Button
            variant="contained"
            disabled={loading || !query.trim()}
            onClick={onSearch}
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            Look up
          </Button>
        </Box>

        {result && (
          <>
            <Divider />
            <Typography variant="body2" color="text.secondary">
              Searched "{searched}" as a{" "}
              {QUERY_TYPE_LABELS[result.resolvedQueryType] ||
                result.resolvedQueryType}
              .
            </Typography>

            {isCollision && (
              <Alert severity="warning">
                {accounts.length} accounts hold this value. This is the
                collision that makes signup report it as already in use.
              </Alert>
            )}
            {accounts.length === 0 && nuxEntries.length === 0 && (
              <Alert severity="success">
                Nothing holds this value — it is free to claim.
              </Alert>
            )}

            {accounts.map((account: AdminLookupIdentityResponse_Account) => (
              <AccountCard
                key={String(account.userId)}
                account={account}
                onDeactivate={() => setPendingDeactivate(account)}
              />
            ))}

            {nuxEntries.length > 0 && (
              <Paper variant="outlined" sx={{ padding: "16px" }}>
                <Typography variant="subtitle2">
                  NUX entries ({nuxEntries.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  NUX claims an invitee email on its own, so one can exist with
                  no account behind it.
                </Typography>
                <Table size="small" sx={{ marginTop: "8px" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invitee email</TableCell>
                      <TableCell>Step</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Linked account</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nuxEntries.map(
                      (nux: AdminLookupIdentityResponse_NuxEntry) => (
                        <TableRow
                          key={`${nux.inviteeEmail}-${String(
                            nux.creationDateSecUTC,
                          )}`}
                        >
                          <TableCell>{nux.inviteeEmail}</TableCell>
                          <TableCell>{nux.stepName || "—"}</TableCell>
                          <TableCell>
                            {formatDate(nux.creationDateSecUTC)}
                          </TableCell>
                          <TableCell>{nux.userRef || "none"}</TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </>
        )}
      </Box>

      <FreeIdentityDialog
        open={!!pendingDeactivate}
        name={pendingDeactivate?.stub?.displayName || ""}
        loading={deactivating}
        onClose={() => setPendingDeactivate(null)}
        onConfirm={onConfirmDeactivate}
      />
    </GridPage>
  );
};
